import { z } from "zod";

export const UserRole = z.string().min(1);
export type UserRole = z.infer<typeof UserRole>;

export const AppPermission = z.enum([
  // admin.* — per-module read/write levels
  "admin.dashboard.read",
  "admin.dashboard.write",
  "admin.users.read",
  "admin.users.write",
  "admin.roles.read",
  "admin.roles.write",
  "admin.bikes.read",
  "admin.bikes.write",
  "admin.bikeCatalog.read",
  "admin.bikeCatalog.write",
  "admin.materials.read",
  "admin.materials.write",
  "admin.locations.read",
  "admin.locations.write",
  "admin.stations.read",
  "admin.stations.write",
  "admin.devices.read",
  "admin.devices.write",
  // admin.* — customer-plane FSM config (ADR-035 §5: carries customer_id, reclassified from settings.*)
  "admin.fsmWorkflows",
  "admin.documentTypes.read",
  "admin.documentTypes.write",
  // requests.*
  "requests.read",
  "requests.write",
  // settings.* — platform-plane, customer-orthogonal only, gates on platform_admin
  "settings.customers",
  // reporting.*
  "reporting.materialsBudget",
  "reporting.kpiOps.read",
  "reporting.kpiOps.write",
  // tracking.* — read-only Order Tracking view (BEL-777)
  "tracking.orderTracking.read",
]);
export type AppPermission = z.infer<typeof AppPermission>;

export const AuthErrorCode = z.enum([
  "INVALID_TOKEN",
  "USER_NOT_FOUND",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "EMAIL_ALREADY_EXISTS",
  "WEAK_PASSWORD",
]);

export const AuthError = z.object({
  code: AuthErrorCode,
  message: z.string(),
});

export type AuthErrorCode = z.infer<typeof AuthErrorCode>;
export type AuthError = z.infer<typeof AuthError>;

export const RegisterUserInput = z.object({
  email: z.string().email(),
  password: z.string().min(6).optional(),
  name: z.string().min(1),
});

export const RegisterUserOutput = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  requiresEmailVerification: z.boolean(),
});

export type RegisterUserInput = z.infer<typeof RegisterUserInput>;
export type RegisterUserOutput = z.infer<typeof RegisterUserOutput>;

export const UpdateUserRoleInput = z.object({
  userId: z.string().uuid(),
  newRole: UserRole,
});

export const SyncJwtInput = z.object({
  userId: z.string().uuid(),
});

export const ManageRoleInput = z.object({
  userId: z.string().uuid(),
  roleEntityId: z.string().uuid(),
});

import { SUPPORTED_LANGUAGES } from "../i18n/index.ts";

export const SupportedLanguageSchema = z.enum(SUPPORTED_LANGUAGES);
export type SupportedLanguageSchema = z.infer<typeof SupportedLanguageSchema>;

export const UpdateProfileInput = z.object({
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  preferredLanguage: SupportedLanguageSchema.optional(),
});

export type UpdateProfileInput = z.infer<typeof UpdateProfileInput>;

export const AdminUpdateUserInput = z.object({
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  role: UserRole.optional(),
});

export type AdminUpdateUserInput = z.infer<typeof AdminUpdateUserInput>;

const SUPER_ADMIN_ROLE_VALUE = "root";

/** Returns true if the given role name is the privileged root role. */
export function isRootRole(role: string): boolean {
  return role === SUPER_ADMIN_ROLE_VALUE;
}

export const InviteUserInput = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: z.string().optional(),
  // BEL-732: which customer the invitee joins. Optional — a caller with a
  // single customer has nothing to choose. When absent the route falls back to
  // the caller's own customer, which used to be the only possible behaviour
  // and meant a user could never be invited into a newly created customer.
  customerId: z.string().uuid().optional(),
});

export type InviteUserInput = z.infer<typeof InviteUserInput>;

export const InviteUserOutput = z.object({
  email: z.string().email(),
  invitationLink: z.string().url(),
});

export type InviteUserOutput = z.infer<typeof InviteUserOutput>;

export const CompleteRegistrationInput = z
  .object({
    name: z.string().min(1),
    password: z
      .string()
      .min(8)
      .regex(/[A-Z]/, "auth.passwordNeedsUppercase")
      .regex(/[0-9]/, "auth.passwordNeedsNumber"),
    confirmPassword: z.string(),
    phone: z.string().min(1).optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "auth.passwordsDoNotMatch",
    path: ["confirmPassword"],
  });

export type CompleteRegistrationInput = z.infer<
  typeof CompleteRegistrationInput
>;
